import React from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { PLRFormProps } from '../../../../../../types/preloan';
import {
  postClaveUnicaHistory,
  postClaveUnicaHistoryAlt,
  patchClaveUnica,
  postPreApprove,
  patchDependentStatus,
  patchSiiIncome,
  patchManualVerification,
} from '../../../../../../queries/preloan/v3';
import {
  FormSchemaV3,
  PatchCheckDocumentsResponse,
  PostClaveUnicaResponse,
} from '../../../../../../types/preloan/v3';
import ClaveUnicaRequest from './components/ClaveUnicaRequest';
import { getInterestRate } from '../../../../../../queries/preloan';
import { ClaveUnicaEndpointProps } from '../../../../../../components/ClaveUnicaForm/redesign';
import type { WorkerType } from '../../../../DependentStatusDialog';
import DependentStatusDialog from '../../../../DependentStatusDialog/redesign';
import { partnerErrorRedirectURL, preLoanRequestStep } from '../../formLogic';
import { returnDataForm, fieldsFromPreApprove } from '../02_dataFetching/components/helpers';
import analytics, { addPreApproveId } from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';
import PlrShell from '../../../landing/PlrShell';
import PlrWaiting from '../../../landing/PlrWaiting';
import { PlrLead, PlrTitle, ProjectLabel } from '../../../landing/ui';
import { partnerFromSchema, useShowHero } from '../../../landing/partner';
import { schemaProjectLabel } from '../../../heroVariant';
import { CU_SOURCES_TEXT } from '../../../legal';
import runInBackground from '../../../../../../utils/runInBackground';

const CALCULATING_MESSAGES = [
  'Leyendo tus ingresos y cotizaciones…',
  'Revisando tus deudas…',
  'Calculando cuánto podrías financiar…',
];

export default function ClaveUnicaForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleStep,
    handleCurrentData,
  } = props;

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorExternalInformation, setErrorExternalInformation] = React.useState(false);
  const [triggerDependentStatusDialog, setTriggerDependentStatusDialog] = React.useState(false);
  const [cuLoading, setCuLoading] = React.useState(false);
  const partner = partnerFromSchema(formSchema);
  const showHero = useShowHero(formSchema);

  React.useEffect(() => {
    if (error) {
      const redirectURL = partnerErrorRedirectURL(
        formSchema.origin,
        formSchema.rut,
        formSchema.config,
      );
      if (redirectURL) {
        setTimeout(() => { window.location.href = redirectURL; }, 1000);
      }
    }
  }, [error]);

  const claveUnicaSuccess = async (
    response: PostClaveUnicaResponse,
  ) => {
    const { data } = response;
    if (response.status === 206) {
      analytics.page(
        inviteePlrCategory(formSchema.complementToken, 'PLR - SITUACION LABORAL'),
        'ELECCION TIPO EMPLEADO',
      );
      return setTriggerDependentStatusDialog(true);
    } if (response.status === 201) {
      analytics.page(
        inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING'),
        'CU INGRESADA EXITOSAMENTE',
      );
    }
    switch (data.plr.step) {
      case 'DATA_OK':
        setLoading(true);
        try {
          const { data: preApproveData } = await postPreApprove(formSchema.PLRtoken);
          if (preApproveData.preApproves?.length > 0) {
            const mainPreApprove = preApproveData.preApproves.find((pa) => pa.productSubtype === 'main');
            const selectedPreApprove = mainPreApprove || preApproveData.preApproves[0];
            addPreApproveId(selectedPreApprove.id);
          }
          switch (preApproveData.plr.step) {
            case 'LOAN_SIMULATION': {
              const interestRate = await getInterestRate(formSchema.PLRtoken);
              handleCurrentData({
                ...formSchema,
                step: preApproveData.plr.step,
                loanParams: interestRate.data,
              });
              return handleStep(preLoanRequestStep.LOAN_SIMULATION);
            }
            case 'READY_FOR_LOAN': {
              handleCurrentData({
                ...formSchema,
                step: preApproveData.plr.step,
                preApproves: preApproveData.preApproves,
                ...fieldsFromPreApprove(preApproveData),
              });
              return handleStep(preLoanRequestStep.READY_FOR_LOAN);
            }
            case 'ARCHIVED_FAIL':
              handleCurrentData({
                ...formSchema,
                step: 'ARCHIVED_FAIL',
                firstName: preApproveData.plr.firstName,
                lastName: preApproveData.plr.lastName,
                rut: preApproveData.plr.idNumber,
                results: preApproveData.results,
                ...fieldsFromPreApprove(preApproveData),
                origin: preApproveData.plr.origin,
              });
              return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
            default:
              return setError(true);
          }
        } catch (e) {
          if (e === 509) {
            analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', {
              initiator: 'backend',
              errorCode: e,
            });
            setErrorExternalInformation(true);
          } else {
            analytics.track('ERROR', {
              initiator: 'backend',
              errorCode: e,
            });
          }
          return setError(true);
        }
      case 'UPLOAD_DOCUMENTS':
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          needsPensionStatement: data.plr.needsPensionStatement,
          needsSettlement: data.plr.needsSettlement,
          settlementsRequired: data.settlementsRequired,
          pensionStatementsRequired: data.pensionStatementsRequired,
        });
        return handleStep(preLoanRequestStep.UPLOAD_DOCUMENTS);
      case 'ARCHIVED_FAIL': {
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          adminComment: data.plr.adminComment,
          firstName: data.plr.firstName,
          lastName: data.plr.lastName,
          rut: data.plr.idNumber,
          origin: data.plr.origin,
          results: data.results,
        });
        return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
      }
      case 'FILL_FORM': {
        const newData = data as PatchCheckDocumentsResponse['data'];
        const rawDataForm = newData.form;
        const { dataForm, contactForm } = returnDataForm({
          rawDataForm,
          phone: data.plr.phone,
          email: data.plr.email,
          formSchema,
        });
        handleCurrentData({
          ...formSchema,
          firstName: data.plr.firstName,
          lastName: data.plr.lastName,
          step: data.plr.step,
          dataForm,
          contactForm,
        });
        return handleStep(preLoanRequestStep.FILL_FORM);
      }
      default:
        return setError(true);
    }
  };

  const postAndPatchClaveUnica = async (params: ClaveUnicaEndpointProps) => {
    const { token, password, code } = params;
    const response = await postClaveUnicaHistory({ token, password, code });

    runInBackground(
      postClaveUnicaHistoryAlt({ token, password }),
      (backgroundError) => {
        Sentry.captureException(backgroundError, {
          tags: { feature: 'clave-unica-history-alt' },
          extra: { preLoanRequestId: formSchema.id },
        });
      },
    );

    if (response.status === 207 || response.status === 208) {
      return response;
    }
    return patchClaveUnica({ token });
  };

  const patchSiiIncomeAction = async (token: string) => {
    const res = await patchSiiIncome({ token, plrId: formSchema.id });
    return res;
  };

  const patchManualVerificationAction = async () => {
    const res = await patchManualVerification({ token: formSchema.PLRtoken });
    const { data } = res;
    switch (data.plr.step) {
      case 'ARCHIVED_FAIL': {
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          firstName: data.plr.firstName,
          lastName: data.plr.lastName,
          rut: data.plr.idNumber,
          results: data.results,
          origin: data.plr.origin,
          adminComment: data.plr.adminComment,
        });
        return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
      }
      default:
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          needsPensionStatement: data.plr.needsPensionStatement,
          needsSettlement: data.plr.needsSettlement,
          settlementsRequired: data.settlementsRequired,
          pensionStatementsRequired: data.pensionStatementsRequired,
        });
        return handleStep(preLoanRequestStep.UPLOAD_DOCUMENTS);
    }
  };

  const patchDependentStatusAction = async (workerType: WorkerType) => {
    const res = await patchDependentStatus({ token: formSchema.PLRtoken, workerType });
    const { data } = res;
    switch (res.data.plr.step) {
      case 'DATA_OK': {
        const { data: preApproveData } = await postPreApprove(formSchema.PLRtoken);
        if (preApproveData.preApproves?.length > 0) {
          const mainPreApprove = preApproveData.preApproves.find((pa) => pa.productSubtype === 'main');
          const selectedPreApprove = mainPreApprove || preApproveData.preApproves[0];
          addPreApproveId(selectedPreApprove.id);
        }
        switch (preApproveData.plr.step) {
          case 'LOAN_SIMULATION': {
            const interestRate = await getInterestRate(formSchema.PLRtoken);
            handleCurrentData({
              ...formSchema,
              step: preApproveData.plr.step,
              loanParams: interestRate.data,
            });
            return handleStep(preLoanRequestStep.LOAN_SIMULATION);
          }
          case 'MANUAL_VERIFICATION':
          case 'READY_FOR_LOAN': {
            handleCurrentData({
              ...formSchema,
              step: preApproveData.plr.step,
              preApproves: preApproveData.preApproves,
              ...fieldsFromPreApprove(preApproveData),
            });
            return handleStep(preLoanRequestStep.READY_FOR_LOAN);
          }
          case 'ARCHIVED_FAIL': {
            handleCurrentData({
              ...formSchema,
              step: preApproveData.plr.step,
              firstName: preApproveData.plr.firstName,
              lastName: preApproveData.plr.lastName,
              rut: preApproveData.plr.idNumber,
              results: preApproveData.results,
              ...fieldsFromPreApprove(preApproveData),
              origin: preApproveData.plr.origin,
              adminComment: preApproveData.plr.adminComment,
            });
            return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
          }
          default:
            return setError(true);
        }
      }
      case 'UPLOAD_DOCUMENTS':
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          needsPensionStatement: data.plr.needsPensionStatement,
          needsSettlement: data.plr.needsSettlement,
          settlementsRequired: data.settlementsRequired,
          pensionStatementsRequired: data.pensionStatementsRequired,
        });
        return handleStep(preLoanRequestStep.UPLOAD_DOCUMENTS);
      case 'NEEDS_ICP':
        handleCurrentData({
          ...formSchema,
          step: data.plr.step,
          needsPensionStatement: data.plr.needsPensionStatement,
          needsSettlement: data.plr.needsSettlement,
        });
        return handleStep(preLoanRequestStep.NEEDS_ICP);
      case 'ARCHIVED_FAIL': {
        const newData = data;
        handleCurrentData({
          ...formSchema,
          adminComment: newData.plr.adminComment,
          firstName: newData.plr.firstName,
          lastName: newData.plr.lastName,
          rut: newData.plr.idNumber,
          origin: newData.plr.origin,
          isRedirect: 'SITUACION LABORAL',
          results: newData.results,
        });
        return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
      }
      case 'FILL_FORM': {
        const newData = data;
        const rawDataForm = newData.form;
        const dataForm = {} as FormSchemaV3['dataForm'];
        const contactForm = {} as FormSchemaV3['contactForm'];
        Object.keys(rawDataForm).forEach((key) => {
          let value: string;
          if (key === 'phone') {
            value = data.plr.phone ? `+56 ${data.plr.phone}` : '+56 ';
            contactForm[key] = { value, type: rawDataForm[key], ok: true };
          } else if (key === 'email') {
            value = data.plr.email || '';
            contactForm[key] = { value, type: rawDataForm[key], ok: true };
          } else {
            value = formSchema.dataForm[key].value;
            dataForm[key] = { value, type: rawDataForm[key] };
          }
        });
        handleCurrentData({
          ...formSchema,
          firstName: data.plr.firstName,
          lastName: data.plr.lastName,
          step: data.plr.step,
          dataForm,
          contactForm,
        });
        return handleStep(preLoanRequestStep.FILL_FORM);
      }
      default:
        return setError(true);
    }
  };
  React.useEffect(() => {
    setTimeout(() => setTriggerDependentStatusDialog(false), 3000);
  }, [triggerDependentStatusDialog]);

  React.useEffect(() => {
    analytics.page(
      inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING'),
      'INGRESO CU',
    );
  }, []);

  const showCalculating = cuLoading || loading || error;
  let calculatingError: string | undefined;
  if (error) {
    calculatingError = errorExternalInformation
      ? 'No pudimos procesar tu evaluación porque uno de nuestros proveedores de información está temporalmente fuera de servicio. Vuelve a intentarlo más tarde.'
      : 'Ha ocurrido un error, por favor intenta en otra ocasión o contáctanos al servicio al cliente.';
  }

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={() => { }}
    >
      {({
        values,
      }) => (
        <Form style={{ width: '100%' }}>
          <PlrShell
            partner={partner}
            showHero={showHero}
            onBack={showCalculating ? undefined : () => handleStep(0)}
            progress={showCalculating
              ? { step: 3, label: 'Tu resultado', partial: true }
              : { step: 2, label: 'Tus ingresos' }}
            testId={showCalculating ? 'preloan-calculating-step' : 'preloan-cu-step'}
          >
            {showHero && (
              <ProjectLabel hideOnPanel>
                {schemaProjectLabel(formSchema)}
              </ProjectLabel>
            )}
            {showCalculating ? (
              <>
                <PlrTitle size="step" sx={{ mt: showHero ? '-6px' : 0 }}>
                  {error ? 'No pudimos calcular tu resultado' : 'Calculando tu resultado'}
                </PlrTitle>
                {!error && <PlrLead>Cerca de 1 minuto. No cierres esta página.</PlrLead>}
                <PlrWaiting
                  testId="preloan-calculating"
                  messages={CALCULATING_MESSAGES}
                  detail={`Revisamos ${CU_SOURCES_TEXT} con tu ClaveÚnica. Solo lectura.`}
                  note="Te llevamos al resultado apenas esté listo."
                  error={calculatingError}
                />
              </>
            ) : (
              <PlrTitle size="step" sx={{ mt: showHero ? '-6px' : 0 }}>
                Ingresa tu ClaveÚnica
              </PlrTitle>
            )}
            {!loading && !error && (
              // flex: 1 para que el consentimiento y "Ver mi resultado" queden pegados al footer.
              <Box sx={{
                display: cuLoading ? 'none' : 'flex', flexDirection: 'column', flex: 1,
              }}
              >
                <ClaveUnicaRequest<PostClaveUnicaResponse>
                  formSchema={values}
                  postClaveUnicaApprove={postAndPatchClaveUnica}
                  claveUnicaSuccess={claveUnicaSuccess}
                  patchSiiIncome={patchSiiIncomeAction}
                  patchDependentStatus={patchDependentStatusAction}
                  patchManualVerification={patchManualVerificationAction}
                  onChangeRut={() => handleStep(0)}
                  onLoadingChange={setCuLoading}
                />
              </Box>
            )}
          </PlrShell>
          <DependentStatusDialog
            action={patchDependentStatusAction}
            isTriggered={triggerDependentStatusDialog}
          />
        </Form>
      )}
    </Formik>
  );
}
